import { useDeferredValue, useEffect, useState } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { useSearch } from '@/api/queries'
import type { SearchResult, SearchResultKind } from '@/api/types'

const KIND_GROUP: Record<SearchResultKind, string> = {
  service: 'Services',
  team: 'Teams',
  person: 'People',
  kb: 'Knowledge Base',
  sop: 'SOPs',
}

/** Static "go to" destinations always reachable from ⌘K, filtered locally by query (the search index is live, these are fixed routes). */
const STATIC_COMMANDS: { label: string; keywords: string; to: string }[] = [
  { label: 'SOP fabric — flowchart library', keywords: 'sop fabric flowchart runbook procedure runner', to: '/knowledge/sops' },
  { label: 'Author a new SOP', keywords: 'sop author new draft flowchart create', to: '/knowledge/sops/new' },
  { label: 'Knowledge base', keywords: 'knowledge kb sop coverage', to: '/knowledge' },
  { label: 'AI · Studio backends', keywords: 'ai studio agent backend model mdi watcher cli openai effort', to: '/ai' },
]

function hrefFor(result: SearchResult): string | undefined {
  switch (result.kind) {
    case 'service':
      return `/fleet/${result.key}`
    case 'kb':
      return `/knowledge?service=${encodeURIComponent(result.key)}`
    case 'sop':
      return `/knowledge?sop=${encodeURIComponent(result.key)}`
    default:
      return undefined
  }
}

/** Global ⌘K search — live over /admin/v2/search (FR-PLT-06). */
export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const { data, isFetching } = useSearch(deferredQuery)
  const navigate = useNavigate()

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const grouped = new Map<string, SearchResult[]>()
  for (const r of data?.results ?? []) {
    const group = KIND_GROUP[r.kind]
    grouped.set(group, [...(grouped.get(group) ?? []), r])
  }

  const q = query.trim().toLowerCase()
  const staticMatches = STATIC_COMMANDS.filter(
    (c) => q === '' || c.label.toLowerCase().includes(q) || c.keywords.includes(q),
  )

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-[440px] max-w-[60vw] cursor-pointer items-center gap-2 rounded-lg border border-border bg-canvas px-3 py-1.5 text-left text-[13px] text-ink-2 hover:border-accent"
      >
        <span className="rounded border border-border bg-surface px-1.5 py-px font-mono text-[11px] text-ink-2">⌘K</span>
        Search services, teams, people…
      </button>
      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Global command menu"
        shouldFilter={false}
        overlayClassName="fixed inset-0 z-[2000] bg-ink/35"
        contentClassName="fixed left-1/2 top-[12vh] z-[2000] w-[600px] max-w-[90vw] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-[0_20px_60px_rgba(17,17,19,0.25)]"
      >
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search services, teams, people…"
          className="w-full border-b border-border px-4 py-3.5 text-[15px] outline-none"
        />
        <Command.List className="max-h-[50vh] overflow-y-auto p-1.5">
          <Command.Empty className="px-2.5 py-2 text-[13px] text-ink-2">
            {query.trim() === '' ? 'Type to search /admin/v2…' : isFetching ? 'Searching…' : 'No results'}
          </Command.Empty>
          {staticMatches.length > 0 && (
            <Command.Group
              heading="Go to"
              className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-0.5 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-unknown [&_[cmdk-group-heading]]:uppercase"
            >
              {staticMatches.map((c) => (
                <Command.Item
                  key={c.to}
                  value={`goto-${c.to}`}
                  onSelect={() => {
                    setOpen(false)
                    navigate(c.to)
                  }}
                  className="flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] data-[selected=true]:bg-accent-weak"
                >
                  <span>{c.label}</span>
                  <span className="font-mono text-[11.5px] text-ink-2">{c.to}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
          {Array.from(grouped.entries()).map(([group, items]) => (
            <Command.Group
              key={group}
              heading={group}
              className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-0.5 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-unknown [&_[cmdk-group-heading]]:uppercase"
            >
              {items.map((item) => (
                <Command.Item
                  key={`${item.kind}-${item.key}`}
                  value={`${item.kind}-${item.key}`}
                  onSelect={() => {
                    setOpen(false)
                    const href = hrefFor(item)
                    if (href) navigate(href)
                  }}
                  className="flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[13px] data-[selected=true]:bg-accent-weak"
                >
                  <span>{item.name}</span>
                  <span className="font-mono text-[11.5px] text-ink-2">{item.key}</span>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
        <div className="flex gap-3.5 border-t border-border px-4 py-2 text-[11px] text-ink-2">
          <span>↵ open</span>
          <span>↑↓ navigate</span>
          <span>esc close</span>
        </div>
      </Command.Dialog>
    </>
  )
}
